(() => {
  const selector = '#SN-Categories-Drawer';
  const instances = new Map();

  function setupImage(image, signal) {
    if (!image || image.dataset.snImageReady === 'true') return;
    image.dataset.snImageReady = 'true';
    let revealRequested = false;
    const fail = () => image.classList.add('is-broken');
    const reveal = async () => {
      if (revealRequested) return;
      revealRequested = true;
      image.classList.remove('is-broken');
      try {
        if (typeof image.decode === 'function') await image.decode();
      } catch (_error) {
        if (!image.complete || image.naturalWidth === 0) {
          fail();
          return;
        }
      }
      if (signal.aborted) return;
      requestAnimationFrame(() => {
        if (!signal.aborted) image.classList.add('is-loaded');
      });
    };
    image.addEventListener('error', fail, { once: true, signal });
    image.addEventListener('load', reveal, { once: true, signal });
    if (!image.hasAttribute('data-src') && image.complete && image.naturalWidth === 0) fail();
    else if (!image.hasAttribute('data-src') && image.complete) reveal();
  }

  function hydrateImage(image, priority = 'auto') {
    if (!image) return;
    if (image.dataset.snCategoryLoading === 'true') {
      if (priority === 'high') image.fetchPriority = 'high';
      return;
    }
    if (!image.dataset.src) return;
    image.dataset.snCategoryLoading = 'true';
    image.loading = 'eager';
    image.fetchPriority = priority;
    if (image.dataset.srcset) image.srcset = image.dataset.srcset;
    image.src = image.dataset.src;
    image.removeAttribute('data-srcset');
    image.removeAttribute('data-src');
  }

  function hydratePanel(panel, priority = 'auto') {
    panel?.querySelectorAll('[data-sn-category-image][data-src]').forEach((image) => hydrateImage(image, priority));
  }

  function initialiseDrawer(drawer) {
    if (!drawer || instances.has(drawer)) return;

    const stage = drawer.querySelector('[data-sn-categories-stage]');
    const dialog = drawer.querySelector('[data-sn-categories-dialog]');
    const rootPanel = drawer.querySelector('[data-sn-categories-panel="root"]');
    const backButton = drawer.querySelector('[data-sn-categories-back]');
    const closeButton = drawer.querySelector('[data-sn-categories-close]');
    if (!stage || !dialog || !rootPanel || !backButton || !closeButton || typeof drawer.open !== 'function' || typeof drawer.close !== 'function') return;

    const controller = new AbortController();
    const signal = controller.signal;
    const originalOpen = drawer.open.bind(drawer);
    const originalClose = drawer.close.bind(drawer);
    let activePanel = rootPanel;
    let levelActivator = null;
    let externalTrigger = null;
    let rootScrollTop = 0;
    let closeRequest = null;
    let preloadFrame = 0;

    const preloadVisiblePanels = () => {
      preloadFrame = 0;
      if (rootPanel.hidden) return;
      const stageRect = stage.getBoundingClientRect();
      rootPanel.querySelectorAll('[data-sn-categories-target]').forEach((button) => {
        const rect = button.getBoundingClientRect();
        const isVisible = rect.bottom > stageRect.top && rect.top < stageRect.bottom;
        if (!isVisible) return;
        hydratePanel(document.getElementById(button.dataset.snCategoriesTarget), 'low');
      });
    };

    const scheduleVisiblePanelPreload = () => {
      if (preloadFrame) return;
      preloadFrame = requestAnimationFrame(preloadVisiblePanels);
    };

    const clearAnimation = (panel) => {
      panel.classList.remove('is-returning');
    };

    const reset = () => {
      drawer.querySelectorAll('[data-sn-categories-target]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
      drawer.querySelectorAll('[data-sn-categories-panel]').forEach((panel) => {
        panel.hidden = panel !== rootPanel;
        clearAnimation(panel);
      });
      activePanel = rootPanel;
      levelActivator = null;
      rootScrollTop = 0;
      stage.scrollTop = 0;
      backButton.hidden = true;
      drawer.classList.remove('is-level-two');
    };

    const showLevelTwo = (button) => {
      const panel = document.getElementById(button.dataset.snCategoriesTarget);
      if (!panel || !drawer.contains(panel) || panel === activePanel) return;
      hydratePanel(panel, 'high');
      rootScrollTop = stage.scrollTop;
      activePanel.hidden = true;
      clearAnimation(panel);
      panel.hidden = false;
      activePanel = panel;
      levelActivator = button;
      button.setAttribute('aria-expanded', 'true');
      backButton.hidden = false;
      drawer.classList.add('is-level-two');
      stage.scrollTop = 0;
      requestAnimationFrame(() => backButton.focus({ preventScroll: true }));
    };

    const showRoot = () => {
      if (activePanel === rootPanel) return;
      activePanel.hidden = true;
      rootPanel.classList.add('is-returning');
      rootPanel.hidden = false;
      activePanel = rootPanel;
      backButton.hidden = true;
      drawer.classList.remove('is-level-two');
      if (levelActivator) levelActivator.setAttribute('aria-expanded', 'false');
      stage.scrollTop = rootScrollTop;
      const focusTarget = levelActivator;
      levelActivator = null;
      requestAnimationFrame(() => {
        focusTarget?.focus({ preventScroll: true });
        scheduleVisiblePanelPreload();
      });
    };

    drawer.open = (triggeredBy) => {
      reset();
      externalTrigger = triggeredBy || null;
      externalTrigger?.setAttribute('aria-expanded', 'true');
      originalOpen(triggeredBy);
      requestAnimationFrame(() => {
        dialog.focus({ preventScroll: true });
        scheduleVisiblePanelPreload();
      });
    };

    drawer.close = () => {
      if (closeRequest) return closeRequest;
      externalTrigger?.setAttribute('aria-expanded', 'false');
      closeRequest = Promise.resolve(originalClose()).finally(() => {
        reset();
        closeRequest = null;
      });
      return closeRequest;
    };

    drawer.addEventListener('click', (event) => {
      const target = event.target.closest('[data-sn-categories-target]');
      if (target && drawer.contains(target)) {
        event.preventDefault();
        showLevelTwo(target);
        return;
      }

      const utility = event.target.closest('[data-sn-reuse-trigger]');
      if (!utility || !drawer.contains(utility)) return;
      event.preventDefault();
      const source = [...document.querySelectorAll(utility.dataset.snReuseTrigger)].find((node) => !drawer.contains(node));
      const fallback = utility.dataset.snFallbackUrl;
      drawer.close().then(() => {
        if (source) source.click();
        else if (fallback) window.location.assign(fallback);
      });
    }, { signal });

    drawer.addEventListener('pointerdown', (event) => {
      const target = event.target.closest('[data-sn-categories-target]');
      if (!target || !drawer.contains(target)) return;
      hydratePanel(document.getElementById(target.dataset.snCategoriesTarget), 'high');
    }, { passive: true, signal });

    stage.addEventListener('scroll', scheduleVisiblePanelPreload, { passive: true, signal });
    backButton.addEventListener('click', showRoot, { signal });
    closeButton.addEventListener('click', () => drawer.close(), { signal });
    drawer.addEventListener('error', (event) => {
      if (event.target.matches?.('[data-sn-category-image]')) event.target.classList.add('is-broken');
    }, { capture: true, signal });
    drawer.querySelectorAll('[data-sn-category-image]').forEach((image) => setupImage(image, signal));

    reset();
    drawer.dataset.snCategoriesReady = 'true';
    instances.set(drawer, {
      destroy() {
        if (preloadFrame) cancelAnimationFrame(preloadFrame);
        controller.abort();
        instances.delete(drawer);
      }
    });
  }

  function initialise(scope = document) {
    if (scope.matches?.(selector)) initialiseDrawer(scope);
    scope.querySelectorAll?.(selector).forEach(initialiseDrawer);
  }

  function destroy(drawer) {
    instances.get(drawer)?.destroy();
  }

  if (window.SNCategories) {
    window.SNCategories.init(document);
    return;
  }

  window.SNCategories = { init: initialise, destroy };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialise(), { once: true });
  else initialise();

  document.addEventListener('shopify:section:load', () => requestAnimationFrame(() => initialise()));
  document.addEventListener('shopify:section:unload', (event) => {
    const sectionId = event.detail?.sectionId;
    if (!sectionId) return;
    document.querySelectorAll(`${selector}[data-section-id="${CSS.escape(sectionId)}"]`).forEach((drawer) => {
      destroy(drawer);
      drawer.remove();
    });
  });
})();
